import {
  EditCalendar as EditCalendarIcon,
  Edit as EditIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import {
  Grid,
  IconButton,
  Link,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { DetailAccordion } from "components/DetailAccordion";
import { EditMachineInstallDateDialog } from "components/job/machine/EditMachineInstallDateDialog";
import { EditMachinePropertiesDialog } from "components/job/machine/EditMachinePropertiesDialog";
import { intlFormatDate, intlFormatDateTimeWithWeekday, isAbortError } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { PropertyValueInputType, UpdateMachineInputType } from "operations/schema/schema";
import { ChangeJobMachineDialog } from "pages/jobs/dialogs/ChangeJobMachineDialog";
import { FC, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { selectConnectionStatus } from "store/root.store";
import { setOpen } from "store/slices/dialog.store";
import {
  getJob,
  selectSelectedJob,
  setChangeJobMachineOpen,
  updateMachineCustomProps,
  updateMachineInstallDate,
} from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { isFlagEnabled } from "store/slices/user.store";

const PREFIX = "JobDetailTab";

const classes = {
  bold: `${PREFIX}-bold`,
  historyIconAdjust: `${PREFIX}-eq-history-icon-adjust`,
  iconAutoMargin: `${PREFIX}-icon-auto-margin`,
  noPadding: `${PREFIX}-no-padding`,
  leftIndent: `${PREFIX}-left-indent`,
  disabled: `${PREFIX}-disabled`,
};

export const MachineDisplay: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectSelectedJob);
  const { editMachineLoading } = useAppSelector((s) => s.jobs);
  const isOnline = useAppSelector(selectConnectionStatus);
  const machineSortGroupsEnabled = useAppSelector((s) => isFlagEnabled(s, "MachineSortGroups"));
  const changeJobMachineEnabled = useAppSelector((s) => isFlagEnabled(s, "ChangeJobMachine"));
  const canChangeMachine =
    technicianSettings.isAllowedMobileMachineEdit && isOnline && changeJobMachineEnabled;
  const canChangeInstallDate =
    technicianSettings.isAllowedMobileMachineEdit && technicianSettings.allowEditMachineInstallDate;
  const showDetailsAccordion = !!job.machine?.details;

  const backTo = pathname.split("/").filter((x) => x)[0];
  const machineInstallDate = job.machine?.installDate;

  const refetch = useCallback(() => {
    dispatch(getJob())
      .then(unwrapResult)
      .catch((e) => {
        if (isAbortError(e)) return;
        dispatch(addSnackbarMessage({ key: "GetJob-fail" }));
      });
  }, [dispatch]);

  const openServiceHistory = useCallback(
    (id: string) => {
      navigate("/history/machine/" + id, {
        state: {
          navTitle: job.machine?.name,
          backTo: backTo,
        },
      });
    },
    [backTo, job.machine?.name, navigate],
  );

  const historyIcon = <HistoryIcon className={classes.historyIconAdjust} />;

  const formatCustomProperty = function (type: string | undefined, value: any) {
    switch (type) {
      case "Boolean":
        return value
          ? intl.formatMessage({ id: "general.yes" })
          : intl.formatMessage({ id: "general.no" });
      case "DateTime":
        return intlFormatDateTimeWithWeekday(intl, value);
      case "Date":
        return intlFormatDate(intl, value);
      case "TimeOfDay":
      case "Decimal":
      case "DropDown":
      case "Int32":
      case "String":
      default:
        return value;
    }
  };

  const updateMachinePropertiesCb = useCallback(
    async (values: PropertyValueInputType[]) => {
      const ret = { success: false, queued: false, abort: false };
      await dispatch(updateMachineCustomProps({ jobId: job.id, properties: values }))
        .then(unwrapResult)
        .then(({ queued }) => {
          ret.success = true;
          ret.queued = true;
        })
        .catch((e) => {
          ret.abort = isAbortError(e);
        });
      return ret;
    },
    [dispatch, job.id],
  );

  const updateMachineInstallDateCb = useCallback(
    async (machine: UpdateMachineInputType) => {
      let ret = { success: false, queued: false, abort: false };
      await dispatch(updateMachineInstallDate({ jobId: job.id, machine }))
        .then(unwrapResult)
        .then(({ queued }) => {
          ret.success = true;
          ret.queued = !!queued;
        })
        .catch((e) => {
          ret.abort = isAbortError(e);
        });
      return ret;
    },
    [dispatch, job.id],
  );

  return (
    <>
      {job.machine?.id && (
        <ListItem
          component="div"
          disableGutters
          className={classes.noPadding}
          data-testid="MachineDisplay"
        >
          <ListItemIcon
            onClick={() => openServiceHistory(job.machine!.id!)}
            className={`min-width-38 ${classes.iconAutoMargin}`}
            sx={{ alignSelf: "flex-start", mt: 1 }}
            data-testid="MachineDisplay-OpenMachineHistory"
          >
            {historyIcon}
          </ListItemIcon>

          <ListItemText
            className={classes.bold}
            primary={
              <>
                {job.machine?.name}
                {canChangeMachine && (
                  <IconButton
                    aria-label={intl.formatMessage({ id: "visit.changeMachine" })}
                    onClick={() => dispatch(setChangeJobMachineOpen({ open: true }))}
                    data-testid="MachineDisplay-ChangeJobMachine"
                    sx={{
                      fontSize: "1.2rem",
                      paddingY: 0,
                    }}
                  >
                    <EditIcon color="primary" />
                  </IconButton>
                )}
              </>
            }
          />
        </ListItem>
      )}

      <div className={classes.leftIndent}>
        {job.machine?.id && (
          <>
            <Typography>{job.machine?.machineNo}</Typography>
            {job.machine?.idNo && <Typography>{job.machine.idNo}</Typography>}
            {job.machine?.location &&
              (job.machine.location.startsWith("///") ? (
                <Link
                  href={`https://what3words.com/${job.machine.location.replace("///", "")}`}
                  underline="none"
                  color="blue"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {job.machine.location}
                </Link>
              ) : (
                <Typography>{job.machine.location}</Typography>
              ))}
            <Typography>
              {machineInstallDate &&
                `${intl.formatMessage({
                  id: "general.installed",
                })} ${intlFormatDate(intl, machineInstallDate)}`}
              {!machineInstallDate && <FormattedMessage id="editMachine.addInstallDate" />}
              {canChangeInstallDate && (
                <IconButton
                  aria-label={intl.formatMessage({ id: "visit.editMachineInstallDate" })}
                  onClick={() =>
                    dispatch(setOpen({ dialogName: "editMachineInstallDate", open: true }))
                  }
                  data-testid="MachineDisplay-ChangeInstallDate"
                  sx={{
                    fontSize: "1.2rem",
                    paddingY: 0,
                  }}
                >
                  <EditCalendarIcon color="primary" />
                </IconButton>
              )}
            </Typography>
          </>
        )}

        {showDetailsAccordion && (
          <DetailAccordion
            data-testid="MachineDisplay-DetailAccordion"
            content={
              <>
                {!!job.machine?.details?.information && (
                  <Typography>
                    {`${intl.formatMessage({
                      id: "job.machine.details.information",
                    })}: ${job.machine.details.information}`}
                  </Typography>
                )}
                {!!job.machine?.details?.customProperties?.length && (
                  <Grid container>
                    <Grid item>
                      {job.machine.details.customProperties.map((cp, index) => (
                        <Typography key={`${cp?.code}-${index}`}>
                          {`${cp?.description}: ${formatCustomProperty(cp?.type, cp?.value)}`}
                        </Typography>
                      ))}
                    </Grid>
                    <Grid item xs={2}>
                      {technicianSettings?.isAllowedMobileMachineEdit && (
                        <EditIcon
                          sx={{ ml: 3 }}
                          onClick={() =>
                            dispatch(setOpen({ dialogName: "editMachineProperties", open: true }))
                          }
                          data-testid="MachineDisplay-OpenEditMachine"
                        />
                      )}
                    </Grid>
                  </Grid>
                )}
                {machineSortGroupsEnabled &&
                  !!job.machine?.details?.sortGroups?.length &&
                  job.machine.details.sortGroups.map((sg, index) => (
                    <Typography key={`sortgroup-${index}`}>
                      {`${sg?.description}: ${sg?.value}`}
                    </Typography>
                  ))}
              </>
            }
          />
        )}
      </div>
      {canChangeMachine && <ChangeJobMachineDialog handleFinished={refetch} />}
      {job.machine && technicianSettings?.isAllowedMobileMachineEdit && (
        <EditMachinePropertiesDialog
          loading={editMachineLoading}
          updateMachineProperties={updateMachinePropertiesCb}
        />
      )}
      {job.machine && canChangeInstallDate && (
        <EditMachineInstallDateDialog
          machine={job.machine}
          externalId={job.externalId}
          loading={editMachineLoading}
          updateMachine={updateMachineInstallDateCb}
        />
      )}
    </>
  );
};
